import React from 'react';
import { Link } from 'react-router-dom';
import {
  Edit, ArrowRightLeft, Tag, Printer, Trash2, ShoppingBag, Store, Warehouse, Package,
} from 'lucide-react';
import MagasinBadge from '../../components/MagasinBadge';
import { fmtEuro, stockPhotoUrl, stockStatusBadge } from './stockUtils';

const RepSection = ({ title, children }) => (
  <section className="rounded-xl border border-white/10 bg-[#151820] p-4">
    {title && <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">{title}</h3>}
    {children}
  </section>
);

const StockDetailPanel = ({
  article,
  promo,
  isAdmin,
  onTransfer,
  onPromo,
  onLabel,
  onDelete,
  onToggleOnline,
}) => {
  if (!article) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500 text-sm p-8 text-center">
        Sélectionnez un article dans la liste
      </div>
    );
  }

  const tone = stockStatusBadge(article.quantite, article.seuil_alerte, article.quantite_depot);
  const thumb = stockPhotoUrl(article.thumbnail_url || article.photo_url);

  return (
    <div className="flex flex-col gap-4 h-full overflow-y-auto" data-testid="stock-detail-panel">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-lg font-bold text-red-400 font-mono">{article.reference}</span>
            <MagasinBadge code={article.magasin_id || 'M1'} size="md" />
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${tone.className}`}>{tone.label}</span>
          </div>
          <p className="text-base font-semibold text-white mt-2">{article.designation}</p>
        </div>
        <Link
          to={`/stock/modifier/${article.id}`}
          className="shrink-0 min-h-[36px] px-3 rounded-lg bg-white/10 hover:bg-white/15 text-xs font-semibold text-gray-200 flex items-center gap-1.5"
          data-testid={`stock-edit-${article.id}`}
        >
          <Edit size={14} /> Modifier
        </Link>
      </div>

      {thumb && (
        <div className="w-24 h-24 rounded-lg border border-white/10 overflow-hidden bg-black/40">
          <img src={thumb} alt="" className="w-full h-full object-contain p-1" />
        </div>
      )}

      <RepSection title="Article">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><div className="text-[10px] text-gray-500 uppercase">Catégorie</div><div className="text-white">{article.categorie || '—'}</div></div>
          <div><div className="text-[10px] text-gray-500 uppercase">Fournisseur</div><div className="text-white truncate">{article.fournisseur || '—'}</div></div>
          {article.ean && <div className="col-span-2"><div className="text-[10px] text-gray-500 uppercase">EAN</div><div className="text-white font-mono text-xs">{article.ean}</div></div>}
        </div>
      </RepSection>

      <RepSection title="Stock">
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-lg bg-black/25 border border-white/10 p-3 text-center">
            <Store size={14} className="mx-auto text-gray-500 mb-1" />
            <div className="text-[10px] text-gray-500 uppercase">Boutique</div>
            <div className="text-xl font-bold text-white">{article.quantite ?? 0}</div>
          </div>
          <div className="rounded-lg bg-black/25 border border-white/10 p-3 text-center">
            <Warehouse size={14} className="mx-auto text-gray-500 mb-1" />
            <div className="text-[10px] text-gray-500 uppercase">Dépôt</div>
            <div className="text-xl font-bold text-gray-200">{article.quantite_depot ?? 0}</div>
          </div>
          <div className="rounded-lg bg-black/25 border border-white/10 p-3 text-center">
            <Package size={14} className="mx-auto text-gray-500 mb-1" />
            <div className="text-[10px] text-gray-500 uppercase">Seuil</div>
            <div className="text-xl font-bold text-amber-300">{article.seuil_alerte ?? 3}</div>
          </div>
        </div>
      </RepSection>

      <RepSection title="Tarifs">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-gray-400">Prix achat</span><span className="text-white tabular-nums">{fmtEuro(article.prix_achat)} €</span></div>
          <div className="flex justify-between">
            <span className="text-gray-400">Prix vente</span>
            {promo ? (
              <span className="text-right">
                <span className="text-xs text-gray-500 line-through block">{fmtEuro(article.prix_vente)} €</span>
                <span className="text-emerald-400 font-bold tabular-nums">{fmtEuro(promo.effective_price)} €</span>
              </span>
            ) : (
              <span className="text-white font-bold tabular-nums">{fmtEuro(article.prix_vente)} €</span>
            )}
          </div>
        </div>
      </RepSection>

      <RepSection title="Actions">
        <div className="flex flex-col gap-2">
          <button type="button" onClick={() => onTransfer?.(article)} className="min-h-[44px] w-full px-4 rounded-lg bg-red-600 hover:bg-red-500 font-semibold text-sm flex items-center justify-center gap-2">
            <ArrowRightLeft size={16} /> Transfert magasin
          </button>
          <button type="button" onClick={() => onPromo?.(article)} className="min-h-[40px] w-full px-4 rounded-lg bg-white/10 hover:bg-white/15 font-semibold text-sm flex items-center justify-center gap-2 text-gray-200">
            <Tag size={16} /> {promo ? `Promo : ${promo.label || 'active'}` : 'Ajouter promotion'}
          </button>
          <button type="button" onClick={() => onLabel?.(article)} className="min-h-[40px] w-full px-4 rounded-lg border border-white/15 hover:bg-white/5 font-semibold text-sm flex items-center justify-center gap-2 text-gray-300">
            <Printer size={16} /> Étiquette
          </button>
          <button
            type="button"
            onClick={() => onToggleOnline?.(article)}
            className="min-h-[40px] w-full px-4 rounded-lg border border-white/15 hover:bg-white/5 font-semibold text-sm flex items-center justify-center gap-2 text-gray-300"
          >
            <ShoppingBag size={16} /> {article.visible_boutique ? 'Retirer en ligne' : 'Mettre en ligne'}
          </button>
          {isAdmin && (
            <button type="button" onClick={() => onDelete?.(article.id)} className="min-h-[40px] w-full px-4 rounded-lg bg-red-950/40 hover:bg-red-950/60 font-semibold text-sm text-red-300 flex items-center justify-center gap-2">
              <Trash2 size={16} /> Supprimer
            </button>
          )}
        </div>
      </RepSection>

      <Link to="/stock/historique" className="text-xs text-gray-500 hover:text-white text-center py-2">
        Voir l&apos;historique des mouvements →
      </Link>
    </div>
  );
};

export default StockDetailPanel;
